// Module-level ticker → asset type cache. Populated opportunistically by
// any data fetcher that already knows the asset type (Navbar search,
// stock-detail load, ETF-detail load, future hot paths). Consulted by
// TickerLink to pick the correct destination URL (/stocks/X vs /etf/X)
// on first paint — no flash, no edge redirect, no useEffect bounce.
//
// This is the "phase 3" optimization on top of the page-level redirect
// (phase 1) and the Next.js middleware edge redirect (phase 2). All
// three layers are independent: cache misses fall through to middleware,
// middleware cold-cache windows fall through to the page-level redirect.
//
// Persisted to localStorage so a tab reload picks up where the last one
// left off — types rarely change (ETF → stock reclassifications happen
// over months, not minutes). A 7-day TTL keeps stale entries from living
// forever if a ticker does get reclassified.

const STORAGE_KEY = 'siq:tickerTypes:v1';
const STORAGE_TTL_MS = 7 * 24 * 60 * 60_000;
const STORAGE_DEBOUNCE_MS = 1000;

interface StorageEnvelope {
  v: 1;
  loadedAtMs: number;
  entries: Array<[string, 'stock' | 'etf']>;
}

const cache = new Map<string, 'stock' | 'etf'>();
let hydrated = false;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function hydrateOnce(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as StorageEnvelope;
    if (parsed?.v !== 1 || !Array.isArray(parsed.entries)) return;
    if (Date.now() - parsed.loadedAtMs > STORAGE_TTL_MS) return;
    for (const [ticker, type] of parsed.entries) {
      if (typeof ticker === 'string' && (type === 'stock' || type === 'etf')) {
        cache.set(ticker.toUpperCase(), type);
      }
    }
  } catch {
    // Corrupt localStorage entry — drop it, start fresh.
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
}

function scheduleSave(): void {
  if (typeof window === 'undefined') return;
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try {
      const envelope: StorageEnvelope = {
        v: 1,
        loadedAtMs: Date.now(),
        entries: Array.from(cache.entries()),
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
    } catch {
      // Quota exceeded or storage disabled — fine, in-memory cache still works.
    }
  }, STORAGE_DEBOUNCE_MS);
}

/** Record what we now know about a ticker. Idempotent: re-asserting the
 *  same type is a no-op (avoids redundant localStorage writes). When the
 *  type CHANGES (reclassification), the cache flips and the next
 *  TickerLink render picks the new URL. */
export function rememberTickerType(ticker: string, type: 'stock' | 'etf'): void {
  hydrateOnce();
  const upper = ticker.toUpperCase();
  if (cache.get(upper) === type) return;
  cache.set(upper, type);
  scheduleSave();
}

/** Bulk-load — used by the boot-time hydration to seed the cache from
 *  the etf-list endpoint in one shot. Anything not in the list is left
 *  unset (TickerLink defaults to /stocks/, middleware catches any
 *  outliers that turn out to be ETFs). */
export function rememberManyTickerTypes(entries: Array<{ ticker: string; type: 'stock' | 'etf' }>): void {
  hydrateOnce();
  let changed = false;
  for (const { ticker, type } of entries) {
    const upper = ticker.toUpperCase();
    if (cache.get(upper) !== type) {
      cache.set(upper, type);
      changed = true;
    }
  }
  if (changed) scheduleSave();
}

/** Returns the cached asset type for a ticker, or undefined when we've
 *  never seen it. TickerLink uses this to pick the URL on first paint
 *  without any async work. */
export function getTickerType(ticker: string): 'stock' | 'etf' | undefined {
  hydrateOnce();
  return cache.get(ticker.toUpperCase());
}

/** Resolve the canonical detail-page href for a ticker given what we
 *  currently know. Defaults to /stocks/ when type is unknown — the
 *  middleware edge redirect and the page-level useEffect both catch
 *  the case where that default turned out to be wrong. */
export function tickerHref(ticker: string, hintType?: 'stock' | 'etf'): string {
  const upper = ticker.toUpperCase();
  const type = hintType ?? getTickerType(upper);
  const prefix = type === 'etf' ? '/etf' : '/stocks';
  return `${prefix}/${upper}`;
}

/** Test-only: nuke the cache so tests don't leak between cases. The
 *  underscore prefix signals "not for runtime use." */
export function __testResetTickerTypeCache(): void {
  cache.clear();
  hydrated = false;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  if (typeof window !== 'undefined') {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
}
